import { useState, useEffect } from 'react';
import { Tabs, Space } from 'antd';
import Table from '../../TableResizable';
import type { TabsProps } from 'antd';
import { Button, Form, Spin, Tooltip } from 'antd';
import { ReloadOutlined, SettingOutlined, TableOutlined, DollarOutlined } from '@ant-design/icons';
import axios from 'axios';
import Tab1 from './Tab1';
import Utilisateur from '../../../Models/Utilisateur';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Data from '../../../Data/Data.json';
import SweetAlert from 'sweetalert2';
import { Crypter } from '../../../Functions/Security/Crypter';
import { Typography, styled } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import { Box } from '@mui/material';

// Modèle Billetage aligné sur la classe C#
interface Billetage {
    key?: number;
    Code: string;
    Libelle: string;
    TypeValeur: string;
    Valeur: number;
    DefaultValue: number;
    ISActif: boolean;
    CreatedBy?: string;
    UpdatedBy?: string;
}

type PanelMode = 'idle' | 'row' | 'adding' | 'editing';
type ViewMode = 'table' | 'currency';

// ─── Composant Billet (SVG) ──────────────────────────────────────────────────
const BilletCard = ({
    item,
    selected,
    onClick,
    disabled,
}: {
    item: Billetage;
    selected: boolean;
    onClick: () => void;
    disabled: boolean;
}) => {
    const isInactif = !item.ISActif;

    // Palette par tranche de valeur
    const getPalette = (valeur: number) => {
        if (valeur >= 10000) return { bg: '#1a5c38', accent: '#f5c842', text: '#fff', stripe: '#14472b' };
        if (valeur >= 5000)  return { bg: '#1a3a6e', accent: '#f5c842', text: '#fff', stripe: '#142e58' };
        if (valeur >= 2000)  return { bg: '#7b1f3a', accent: '#f5e0a0', text: '#fff', stripe: '#621833' };
        if (valeur >= 1000)  return { bg: '#5a3500', accent: '#f5c842', text: '#fff', stripe: '#472900' };
        if (valeur >= 500)   return { bg: '#2d4a2d', accent: '#c8e6a0', text: '#fff', stripe: '#223822' };
        // Pièce (petites valeurs)
        return { bg: '#7a6020', accent: '#f5e0a0', text: '#fff', stripe: '#5c4a18' };
    };

    const pal = getPalette(item.Valeur);
    const isPiece = item.TypeValeur === 'PIECE';

    return (
        <div
            onClick={disabled ? undefined : onClick}
            style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: isInactif ? 0.45 : 1,
                filter: isInactif ? 'grayscale(60%)' : 'none',
                transform: selected ? 'translateY(-6px) scale(1.03)' : 'none',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s',
                flexShrink: 0,
            }}
        >
            {isPiece ? (
                <PieceCard item={item} pal={pal} selected={selected} />
            ) : (
                <BilletSVG item={item} pal={pal} selected={selected} />
            )}
        </div>
    );
};

// ─── SVG Billet ──────────────────────────────────────────────────────────────
const BilletSVG = ({
    item,
    pal,
    selected,
}: {
    item: Billetage;
    pal: { bg: string; accent: string; text: string; stripe: string };
    selected: boolean;
}) => (
    <svg
        width="210"
        height="95"
        viewBox="0 0 210 95"
        style={{
            borderRadius: 8,
            boxShadow: selected
                ? `0 0 0 3px ${pal.accent}, 0 8px 24px rgba(0,0,0,0.4)`
                : '0 4px 14px rgba(0,0,0,0.28)',
            display: 'block',
        }}
        xmlns="http://www.w3.org/2000/svg"
    >
        {/* Fond principal */}
        <rect width="210" height="95" rx="8" fill={pal.bg} />

        {/* Bandes décoratives gauche */}
        <rect x="0" y="0" width="32" height="95" rx="8" fill={pal.stripe} />
        <rect x="28" y="0" width="4" height="95" fill={pal.accent} opacity="0.6" />

        {/* Bandes décoratives droite */}
        <rect x="178" y="0" width="32" height="95" rx="8" fill={pal.stripe} />
        <rect x="178" y="0" width="4" height="95" fill={pal.accent} opacity="0.6" />

        {/* Motif rosace centrale (cercles concentriques) */}
        <circle cx="105" cy="47" r="34" fill="none" stroke={pal.accent} strokeWidth="0.6" opacity="0.25" />
        <circle cx="105" cy="47" r="28" fill="none" stroke={pal.accent} strokeWidth="0.5" opacity="0.2" />
        <circle cx="105" cy="47" r="20" fill="none" stroke={pal.accent} strokeWidth="0.4" opacity="0.15" />

        {/* Lignes diagonales décoratives */}
        {[-20, 0, 20, 40, 60].map((offset, i) => (
            <line
                key={i}
                x1={38 + offset}
                y1="0"
                x2={10 + offset}
                y2="95"
                stroke={pal.accent}
                strokeWidth="0.4"
                opacity="0.1"
            />
        ))}

        {/* Valeur principale — gauche */}
        <text x="16" y="34" textAnchor="middle" fill={pal.accent} fontSize="11" fontWeight="bold" fontFamily="serif" transform="rotate(-90,16,34)" dy="4">
            {item.Valeur.toLocaleString('fr-FR')}
        </text>

        {/* Valeur principale — centre */}
        <text x="105" y="42" textAnchor="middle" fill={pal.accent} fontSize="26" fontWeight="bold" fontFamily="Georgia, serif" letterSpacing="-0.5">
            {item.Valeur >= 1000
                ? `${(item.Valeur / 1000).toLocaleString('fr-FR')}K`
                : item.Valeur.toLocaleString('fr-FR')}
        </text>
        {/* <text x="105" y="57" textAnchor="middle" fill={pal.accent} fontSize="8.5" fontFamily="Georgia, serif" opacity="0.85" letterSpacing="1.5">
            FRANCS CFA
        </text> */}

        {/* Code & Libellé */}
        <text x="105" y="72" textAnchor="middle" fill={pal.text} fontSize="7" fontFamily="monospace" opacity="0.6">
            {item.Code} — {item.Libelle}
        </text>

        {/* Valeur droite (miroir) */}
        <text x="194" y="62" textAnchor="middle" fill={pal.accent} fontSize="11" fontWeight="bold" fontFamily="serif" transform="rotate(90,194,62)" dy="4">
            {item.Valeur.toLocaleString('fr-FR')}
        </text>

        {/* Badge actif/inactif */}
        {!item.ISActif && (
            <>
                <rect x="60" y="40" width="90" height="16" rx="3" fill="rgba(180,0,0,0.7)" />
                <text x="105" y="51.5" textAnchor="middle" fill="#fff" fontSize="8" fontFamily="monospace" fontWeight="bold" letterSpacing="1">
                    INACTIF
                </text>
            </>
        )}

        {/* Sélection ring */}
        {selected && (
            <rect x="1" y="1" width="208" height="93" rx="7.5" fill="none" stroke={pal.accent} strokeWidth="2.5" />
        )}
    </svg>
);

// ─── SVG Pièce ───────────────────────────────────────────────────────────────
const PieceCard = ({
    item,
    pal,
    selected,
}: {
    item: Billetage;
    pal: { bg: string; accent: string; text: string; stripe: string };
    selected: boolean;
}) => (
    <svg
        width="95"
        height="95"
        viewBox="0 0 95 95"
        style={{
            borderRadius: '50%',
            boxShadow: selected
                ? `0 0 0 3px ${pal.accent}, 0 8px 24px rgba(0,0,0,0.4)`
                : '0 4px 14px rgba(0,0,0,0.28)',
            display: 'block',
        }}
        xmlns="http://www.w3.org/2000/svg"
    >
        {/* Corps pièce */}
        <circle cx="47.5" cy="47.5" r="47.5" fill={pal.bg} />
        <circle cx="47.5" cy="47.5" r="43" fill={pal.stripe} />
        <circle cx="47.5" cy="47.5" r="39" fill={pal.bg} />

        {/* Décor dentelé (traits radiaux) */}
        {Array.from({ length: 24 }).map((_, i) => {
            const angle = (i * 360) / 24;
            const rad = (angle * Math.PI) / 180;
            const x1 = 47.5 + 40 * Math.cos(rad);
            const y1 = 47.5 + 40 * Math.sin(rad);
            const x2 = 47.5 + 43 * Math.cos(rad);
            const y2 = 47.5 + 43 * Math.sin(rad);
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={pal.accent} strokeWidth="1.2" opacity="0.5" />;
        })}

        {/* Valeur */}
        <text x="47.5" y="43" textAnchor="middle" fill={pal.accent} fontSize="20" fontWeight="bold" fontFamily="Georgia, serif">
            {item.Valeur}
        </text>
        {/* <text x="47.5" y="55" textAnchor="middle" fill={pal.accent} fontSize="6.5" fontFamily="serif" letterSpacing="1" opacity="0.9">
            FRANCS
        </text> */}
        <text x="47.5" y="65" textAnchor="middle" fill={pal.text} fontSize="5.5" fontFamily="monospace" opacity="0.55">
            {item.Code}
        </text>

        {/* Badge inactif */}
        {!item.ISActif && (
            <>
                <rect x="14" y="41" width="67" height="13" rx="2" fill="rgba(180,0,0,0.75)" />
                <text x="47.5" y="51" textAnchor="middle" fill="#fff" fontSize="7.5" fontFamily="monospace" fontWeight="bold" letterSpacing="1">
                    INACTIF
                </text>
            </>
        )}

        {/* Sélection ring */}
        {selected && (
            <circle cx="47.5" cy="47.5" r="46" fill="none" stroke={pal.accent} strokeWidth="2.5" />
        )}
    </svg>
);

// ─── Vue Monnaie ─────────────────────────────────────────────────────────────
const CurrencyView = ({
    data,
    selectedKey,
    onSelect,
    disabled,
}: {
    data: Billetage[];
    selectedKey: React.Key | null;
    onSelect: (key: number, item: Billetage) => void;
    disabled: boolean;
}) => {
    const billets = data.filter((d) => d.TypeValeur !== 'PIECE').sort((a, b) => b.Valeur - a.Valeur);
    const pieces  = data.filter((d) => d.TypeValeur === 'PIECE').sort((a, b) => b.Valeur - a.Valeur);

    return (
        <div
            style={{
                padding: '12px 8px',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                borderRadius: 10,
                minHeight: 220,
                opacity: disabled ? 0.45 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                transition: 'opacity 0.2s',
                border: '1px solid rgba(255,255,255,0.08)',
            }}
        >
            {billets.length > 0 && (
                <>
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, fontFamily: 'monospace', letterSpacing: 2, marginBottom: 8, paddingLeft: 4 }}>
                        BILLETS
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                        {billets.map((item) => (
                            <BilletCard
                                key={item.key}
                                item={item}
                                selected={selectedKey === item.key}
                                onClick={() => onSelect(item.key as number, item)}
                                disabled={disabled}
                            />
                        ))}
                    </div>
                </>
            )}

            {pieces.length > 0 && (
                <>
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, fontFamily: 'monospace', letterSpacing: 2, marginBottom: 8, paddingLeft: 4 }}>
                        PIÈCES
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                        {pieces.map((item) => (
                            <BilletCard
                                key={item.key}
                                item={item}
                                selected={selectedKey === item.key}
                                onClick={() => onSelect(item.key as number, item)}
                                disabled={disabled}
                            />
                        ))}
                    </div>
                </>
            )}

            {data.length === 0 && (
                <div style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', paddingTop: 60, fontSize: 13 }}>
                    Aucun billetage chargé
                </div>
            )}
        </div>
    );
};

// ─── Composant principal ─────────────────────────────────────────────────────
const BilletageConfig = () => {
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
    const authHeader = useAuthHeader();

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [sbmit, setSbmit] = useState<'add' | 'update' | ''>('');
    const [data, setData] = useState<Billetage[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [mode, setMode] = useState<PanelMode>('idle');
    const [viewMode, setViewMode] = useState<ViewMode>('table');

    const isFormActive  = mode === 'adding' || mode === 'editing';
    const tableDisabled = isFormActive;

    const btnNouveau   = { disabled: false };
    const btnModifier  = { disabled: mode !== 'row' };
    const btnEnreg     = { disabled: !isFormActive };
    const btnDesactiver= { disabled: mode !== 'row' };
    const btnActualiser= { disabled: isFormActive };
    const btnAnnuler   = { disabled: !isFormActive };

    const ThemeTable = styled(Table)(() => ({
        '.ant-table-body': {
            height: '185px',
            borderBottom: '1px var(--colorOpaque020) solid',
        },
    }));

    useEffect(() => { loadBilletages(); }, []);

    function loadBilletages() {
        setLoading(true);
        setData([]);
        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: Data.ApiUrl.GetBilletage,
            headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader(),
                CypherSearch: Crypter.encrypt(''),
            },
        };
        axios(config)
            .then((response) => {
                setLoading(false);
                if (response.data) {
                    const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                    if (res.StatutCode === 200) {
                        const datas: Billetage[] = res.Result.Table;
                        if (datas.length === 0) {
                            SweetAlert.fire({ title: 'Aucun billetage trouvé', icon: 'warning' });
                        } else {
                            setData(datas.map((item, index) => ({
                                key: index,
                                Code: item.Code,
                                Libelle: item.Libelle,
                                TypeValeur: item.TypeValeur,
                                Valeur: item.Valeur,
                                DefaultValue: item.DefaultValue,
                                ISActif: item.ISActif,
                            })));
                        }
                    } else {
                        SweetAlert.fire({ title: res.Message, icon: 'warning' });
                    }
                }
            })
            .catch((error) => {
                setLoading(false);
                SweetAlert.fire({ title: 'Erreur', text: error, icon: 'error' });
            });
    }

    function reset() {
        form.resetFields();
        setSelectedRowKeys([]);
        setSbmit('');
        setMode('idle');
    }

    function ajoutButton() {
        setSbmit('add');
        setMode('adding');
        form.resetFields();
        setSelectedRowKeys([]);
    }

    function modifButton() {
        setSbmit('update');
        setMode('editing');
    }

    function handleAnnuler() { reset(); }

    function handleActualiser() {
        reset();
        loadBilletages();
    }

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        if (isFormActive) return;
        const index = newSelectedRowKeys[0] as number;
        const selected = data[index];
        if (selected) {
            form.setFieldsValue({
                Code: selected.Code,
                Libelle: selected.Libelle,
                TypeValeur: selected.TypeValeur,
                Valeur: selected.Valeur,
                DefaultValue: selected.DefaultValue,
                ISActif: selected.ISActif ? '1' : '0',
            });
            setMode('row');
        } else {
            form.resetFields();
            setMode('idle');
        }
        setSelectedRowKeys(newSelectedRowKeys);
    };

    // Sélection depuis la vue monnaie
    const onCurrencySelect = (index: number, item: Billetage) => {
        if (isFormActive) return;
        if (selectedRowKeys[0] === index) {
            // Déselection au reclique
            form.resetFields();
            setSelectedRowKeys([]);
            setMode('idle');
            return;
        }
        form.setFieldsValue({
            Code: item.Code,
            Libelle: item.Libelle,
            TypeValeur: item.TypeValeur,
            Valeur: item.Valeur,
            DefaultValue: item.DefaultValue,
            ISActif: item.ISActif ? '1' : '0',
        });
        setSelectedRowKeys([index]);
        setMode('row');
    };

    const rowSelection = { selectedRowKeys, onChange: onSelectChange };

    const onFinish = (values: any) => {
        const isAdd = sbmit === 'add';
        SweetAlert.fire({
            title: `Voulez-vous ${isAdd ? 'créer' : 'modifier'} le billetage "${values.Code}" ?`,
            showCancelButton: true,
            confirmButtonText: 'Oui',
            confirmButtonColor: 'green',
            cancelButtonColor: 'darkred',
            cancelButtonText: 'Non',
            icon: 'question',
        }).then((res) => {
            if (res.isConfirmed) {
                const billetage: Billetage = {
                    Code: values.Code,
                    Libelle: values.Libelle,
                    TypeValeur: values.TypeValeur,
                    Valeur: Number(values.Valeur),
                    DefaultValue: Number(values.DefaultValue),
                    ISActif: values.ISActif === '1' || values.ISActif === true,
                    CreatedBy: isAdd ? user.coduti : undefined,
                    UpdatedBy: !isAdd ? user.coduti : undefined,
                };
                const dta = Crypter.encrypt(JSON.stringify(billetage));
                const config = {
                    method: isAdd ? 'post' : 'put',
                    maxBodyLength: Infinity,
                    url: isAdd ? Data.ApiUrl.CreateBilletage : Data.ApiUrl.UpdateBilletage,
                    headers: { Authorization: authHeader(), 'Content-Type': 'application/json' },
                    data: dta,
                };
                setLoading(true);
                axios.request(config)
                    .then((response) => {
                        setLoading(false);
                        if (response.data) {
                            const result = JSON.parse(Crypter.decrypt(response.data)).Value;
                            if (result.StatutCode === 200) {
                                SweetAlert.fire({ title: result.Message, icon: 'success' });
                                reset();
                                loadBilletages();
                            } else {
                                SweetAlert.fire({ title: result.Message, icon: 'warning' });
                            }
                        }
                    })
                    .catch((error) => {
                        setLoading(false);
                        SweetAlert.fire({ title: 'Erreur', text: error, icon: 'error' });
                    });
            }
        });
    };

    const onFinishFailed = (errorInfo: any) => {
        const errors = errorInfo.errorFields.map((e: any) => e.name).join(' - ');
        SweetAlert.fire({
            title: 'Veuillez remplir tous les champs obligatoires',
            text: errors,
            icon: 'warning',
            confirmButtonText: 'OK',
        });
    };

    function handleSupprimer() {
        const code = form.getFieldValue('Code');
        if (!code) {
            SweetAlert.fire({ title: 'Veuillez sélectionner un billetage', icon: 'warning' });
            return;
        }
        SweetAlert.fire({
            title: `Désactiver le billetage "${code}" ?`,
            showCancelButton: true,
            confirmButtonText: 'Oui',
            confirmButtonColor: 'darkred',
            cancelButtonColor: 'gray',
            cancelButtonText: 'Non',
            icon: 'warning',
        }).then((res) => {
            if (res.isConfirmed) {
                const dta = Crypter.encrypt(code);
                const config = {
                    method: 'delete',
                    maxBodyLength: Infinity,
                    url: Data.ApiUrl.DeleteBilletage,
                    headers: {
                        Authorization: authHeader(),
                        'Content-Type': 'application/json',
                        CipherCode: dta,
                    },
                };
                setLoading(true);
                axios.request(config)
                    .then((response) => {
                        setLoading(false);
                        if (response.data) {
                            const result = JSON.parse(Crypter.decrypt(response.data)).Value;
                            SweetAlert.fire({ title: result.Message, icon: result.StatutCode === 200 ? 'success' : 'warning' });
                            if (result.StatutCode === 200) { reset(); loadBilletages(); }
                        }
                    })
                    .catch((error) => {
                        setLoading(false);
                        SweetAlert.fire({ title: 'Erreur', text: error, icon: 'error' });
                    });
            }
        });
    }

    const columns: ColumnsType<Billetage> = [
        { title: 'Code', dataIndex: 'Code', width: 120 },
        { title: 'Libellé', dataIndex: 'Libelle' },
        { title: 'Valeur', dataIndex: 'Valeur' },
        {
            title: 'Type Valeur',
            dataIndex: 'TypeValeur',
            render: (val: string) => (
                <div style={{
                    fontWeight: 'bold',
                    color: val === 'PIECE' ? 'blue' : 'green',
                    backgroundColor: val === 'PIECE' ? 'lightblue' : 'lightgreen',
                    padding: '5px',
                    borderRadius: '5px',
                }}>
                    {val}
                </div>
            ),
        },
        { title: 'Valeur Défaut', dataIndex: 'DefaultValue', width: 130 },
        {
            title: 'Actif',
            dataIndex: 'ISActif',
            width: 80,
            render: (val: boolean) => (val ? '✔' : '✘'),
        },
    ];

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: (
                <span id="Tab">
                    <SettingOutlined />
                    Détail Billetage
                </span>
            ),
            children: <Tab1 form={form} />,
        },
    ];

    // ─── Sélecteur de vue (Tableau / Monnaie) ────────────────────────────────
    const ViewToggle = () => (
        <div style={{ display: 'flex', gap: 4, marginBottom: 6, justifyContent: 'flex-end' }}>
            <Tooltip title="Vue tableau">
                <Button
                    size="small"
                    type={viewMode === 'table' ? 'primary' : 'default'}
                    icon={<TableOutlined />}
                    onClick={() => setViewMode('table')}
                />
            </Tooltip>
            <Tooltip title="Vue monnaie">
                <Button
                    size="small"
                    type={viewMode === 'currency' ? 'primary' : 'default'}
                    icon={<DollarOutlined />}
                    onClick={() => setViewMode('currency')}
                />
            </Tooltip>
        </div>
    );

    return (
        <div className="tab">
            <Typography align="center" className="formTitle">
                <b>CONFIGURATION DU BILLETAGE</b>
            </Typography>

            <Spin spinning={loading} size="large">
                <Form
                    disabled={!isFormActive}
                    autoComplete="off"
                    labelAlign="left"
                    form={form}
                    name="billetage_form"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    style={{ minWidth: 'auto', maxWidth: 'auto' }}
                >
                    {/* ── Panneau actions ──────────────────────────────────── */}
                    <div className="bG">
                        <legend>
                            <Typography color="darkgreen">Actions</Typography>
                        </legend>
                        <Form.Item shouldUpdate className="bouttong">
                            <Space direction="vertical" style={{ width: '100%' }}>

                                <Button size="large" className="bt_add" {...btnNouveau}
                                    onClick={ajoutButton} type="ghost" htmlType="button" block>
                                    Nouveau
                                </Button>

                                <Button size="large" className="bt_add" {...btnModifier}
                                    onClick={modifButton} type="ghost" htmlType="button" block>
                                    Modifier
                                </Button>

                                <Button size="large" className="bt_add" {...btnEnreg}
                                    type="ghost" htmlType="submit" block>
                                    Enregistrer
                                </Button>

                                <Button size="large" className="bt_add" {...btnDesactiver}
                                    onClick={handleSupprimer} type="ghost" htmlType="button" block>
                                    Désactiver
                                </Button>

                                <Button size="large" className="bt_add" {...btnActualiser}
                                    onClick={handleActualiser} type="ghost" htmlType="button"
                                    icon={<ReloadOutlined />} block>
                                    Actualiser
                                </Button>

                                <Button size="large" className="bt_add" {...btnAnnuler}
                                    onClick={handleAnnuler} type="ghost" htmlType="reset" block>
                                    Annuler
                                </Button>

                            </Space>
                        </Form.Item>
                    </div>

                    {/* ── Zone principale ──────────────────────────────────── */}
                    <div className="clientP">
                        <div>
                            {/* Formulaire (onglet) */}
                            <Tabs size="small" type="card" defaultActiveKey="1" items={items} />

                            {/* Zone tableau/monnaie */}
                            <Box
                                fontStyle="inherit"
                                className="border-left tabElementAuto"
                                sx={{ marginTop: '4px' }}
                            >
                                <Spin spinning={loading}>
                                    <Form disabled={false}>
                                        <ViewToggle />
                                    </Form>

                                    {viewMode === 'table' ? (
                                        <div style={{
                                            opacity: tableDisabled ? 0.45 : 1,
                                            pointerEvents: tableDisabled ? 'none' : 'auto',
                                            transition: 'opacity 0.2s',
                                        }}>
                                            <Form disabled={tableDisabled}>
                                                <Table
                                                    size="small"
                                                    style={{ width: '100%' }}
                                                    rowSelection={{ ...rowSelection, type: 'radio' }}
                                                    columns={columns}
                                                    dataSource={data}
                                                    scroll={{ y: 200, x: 'max-content' }}
                                                    pagination={false}
                                                />
                                            </Form>
                                        </div>
                                    ) : (
                                        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                            <CurrencyView
                                                data={data}
                                                selectedKey={selectedRowKeys[0] ?? null}
                                                onSelect={onCurrencySelect}
                                                disabled={tableDisabled}
                                            />
                                        </div>
                                    )}
                                </Spin>
                            </Box>
                        </div>
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default BilletageConfig;